// components/settings/AccountSettingsPage.tsx
'use client';

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useUser } from '@clerk/nextjs';
import { MemorySettingsSection } from '@/components/doctrine/MemoryPane';
import { toast } from 'sonner';

import UserAvatar from '@/components/UserAvatar';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const E164_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

interface AccountSettingsPageProps {
  view?: 'profile' | 'security' | 'all';
}

const translations = {
  en: {
    title: 'Account',
    subtitle: 'Manage your personal details and sign-in credentials.',
    profile: 'Profile',
    photo: 'Photo',
    name: 'Name',
    email: 'Email address',
    avatarHint: 'Click the avatar to upload a new photo.',
    fallbackName: 'User',
    phone: 'Phone number',
    phoneHint: 'Use the international E.164 format, including the + sign.',
    phonePlaceholder: '+14155552671',
    savePhone: 'Save phone number',
    saving: 'Saving…',
    phoneRequired: 'Enter a phone number.',
    phoneInvalid: 'Enter a valid international number, for example +14155552671.',
    phoneUnchanged: 'Your phone number is already up to date.',
    phoneSaved: 'Phone number updated.',
    phoneSaveFailed: 'Could not update the phone number.',
    security: 'Security',
    signIn: 'Sign-in',
    password: 'Password',
    passwordExistingHint: 'Enter your current password before choosing a new one.',
    passwordNewHint: 'Create a password for this account.',
    impersonationHint: 'Password changes are disabled while impersonating another user.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    updatePassword: 'Update password',
    updating: 'Updating…',
    passwordRequired: 'Enter and confirm a new password.',
    currentPasswordRequired: 'Enter your current password.',
    passwordMismatch: 'The new passwords do not match.',
    passwordUpdated: 'Password updated.',
    passwordUpdateFailed: 'Could not update the password.',
  },
  fr: {
    title: 'Compte',
    subtitle: 'Gérez vos informations personnelles et vos identifiants de connexion.',
    profile: 'Profil',
    photo: 'Photo',
    name: 'Nom',
    email: 'Adresse e-mail',
    avatarHint: 'Cliquez sur l’avatar pour importer une nouvelle photo.',
    fallbackName: 'Utilisateur',
    phone: 'Numéro de téléphone',
    phoneHint: 'Utilisez le format international E.164, en incluant le signe +.',
    phonePlaceholder: '+33612345678',
    savePhone: 'Enregistrer le numéro',
    saving: 'Enregistrement…',
    phoneRequired: 'Saisissez un numéro de téléphone.',
    phoneInvalid: 'Saisissez un numéro international valide, par exemple +33612345678.',
    phoneUnchanged: 'Votre numéro de téléphone est déjà à jour.',
    phoneSaved: 'Numéro de téléphone mis à jour.',
    phoneSaveFailed: 'Impossible de mettre à jour le numéro de téléphone.',
    security: 'Sécurité',
    signIn: 'Connexion',
    password: 'Mot de passe',
    passwordExistingHint: 'Saisissez votre mot de passe actuel avant d’en choisir un nouveau.',
    passwordNewHint: 'Créez un mot de passe pour ce compte.',
    impersonationHint: 'La modification du mot de passe est désactivée pendant l’impersonation.',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    updatePassword: 'Mettre à jour le mot de passe',
    updating: 'Mise à jour…',
    passwordRequired: 'Saisissez et confirmez un nouveau mot de passe.',
    currentPasswordRequired: 'Saisissez votre mot de passe actuel.',
    passwordMismatch: 'Les nouveaux mots de passe ne correspondent pas.',
    passwordUpdated: 'Mot de passe mis à jour.',
    passwordUpdateFailed: 'Impossible de mettre à jour le mot de passe.',
  },
} as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readErrorMessage = (error: unknown, fallback: string): string => {
  if (isRecord(error)) {
    const errors = error.errors;
    if (Array.isArray(errors) && isRecord(errors[0])) {
      if (typeof errors[0].longMessage === 'string') return errors[0].longMessage;
      if (typeof errors[0].message === 'string') return errors[0].message;
    }
    if (typeof error.message === 'string') return error.message;
  }

  return fallback;
};

const SETTINGS_ROW_CLASS_NAME = 'grid min-h-[60px] grid-cols-1 gap-2 py-3 lg:grid-cols-[minmax(0,1fr)_minmax(240px,320px)] lg:gap-[clamp(16px,2.5vmin,32px)]';
const INPUT_CLASS_NAME = 'h-10 w-full rounded-full border border-[#2F2F2F] bg-transparent px-4 text-[15px] text-[#ECECEC] outline-none transition-colors placeholder:text-[#8E8E8E] focus:border-[#8E8E8E] disabled:cursor-not-allowed disabled:opacity-45';
// ChatGPT's settings action: a filled #212121 pill; 15px pinned inline where used (the global clamp).
const BUTTON_CLASS_NAME = 'inline-flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#212121] px-4 text-[#ECECEC] transition-colors hover:bg-[#2F2F2F] disabled:cursor-not-allowed disabled:opacity-35';
const BUTTON_STYLE = { fontSize: 15 }; // inputs share it: the clamp reaches them too

const AccountSettingsPage = memo(function AccountSettingsPage({ view = 'all' }: AccountSettingsPageProps) {
  const { kernel } = useKernel();
  const { isLoaded: isClerkLoaded, user } = useUser();
  const language = useKernelStore((state) => state.language);
  const userContext = useKernelStore((state) => state.userContext);
  const currentMember = useKernelStore((state) => {
    const userId = state.userContext.dbUserId;
    return userId ? state.inventory.User.registry[userId] ?? null : null;
  });
  const t = translations[language];

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const isImpersonating = Boolean(userContext.isImpersonating);
  const canEditProfile = Boolean(userContext.dbUserId);
  const canUpdatePassword = isClerkLoaded && Boolean(user) && !isImpersonating;

  const displayName = useMemo(() => {
    const databaseName = `${currentMember?.firstName ?? ''} ${currentMember?.lastName ?? ''}`.trim();
    if (databaseName) return databaseName;
    if (isImpersonating && userContext.impersonatedUser?.name) {
      return userContext.impersonatedUser.name;
    }
    if (user?.fullName) return user.fullName;
    if (user?.username) return user.username;
    const emailName = currentMember?.email?.split('@')[0];
    return emailName || t.fallbackName;
  }, [
    currentMember?.email,
    currentMember?.firstName,
    currentMember?.lastName,
    isImpersonating,
    t.fallbackName,
    user?.fullName,
    user?.username,
    userContext.impersonatedUser?.name,
  ]);

  const email = currentMember?.email
    ?? (isImpersonating ? userContext.impersonatedUser?.email : user?.primaryEmailAddress?.emailAddress)
    ?? '';

  useEffect(() => {
    setPhoneNumber(currentMember?.phoneNumber ?? '');
    setPhoneError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  }, [currentMember?.id, currentMember?.phoneNumber, isImpersonating]);

  const handlePhoneChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
    setPhoneError(null);
  }, []);

  const handleCurrentPasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(event.target.value);
    setPasswordError(null);
  }, []);

  const handleNewPasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setPasswordError(null);
  }, []);

  const handleConfirmPasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
    setPasswordError(null);
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    const userId = kernel.current?.getUserContext().dbUserId;
    if (!userId) return;
    await kernel.current?.syncEntity('User', userId, { force: true });
  }, [kernel]);

  const handleAvatarUpdated = useCallback(() => {
    void refreshCurrentUser();
  }, [refreshCurrentUser]);

  const handlePhoneSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!kernel.current?.getUserContext().dbUserId || isSavingPhone) return;

    const normalizedPhone = phoneNumber.trim();
    if (!normalizedPhone) {
      setPhoneError(t.phoneRequired);
      return;
    }
    if (!E164_PHONE_REGEX.test(normalizedPhone)) {
      setPhoneError(t.phoneInvalid);
      return;
    }

    const effectiveUserId = kernel.current.getUserContext().dbUserId;
    if (!effectiveUserId) return;
    const savedPhone = kernel.current.getState().inventory.User.registry[effectiveUserId]?.phoneNumber?.trim();
    if (normalizedPhone === savedPhone) {
      toast.info(t.phoneUnchanged);
      return;
    }

    setPhoneError(null);
    setIsSavingPhone(true);
    const toastId = toast.loading(t.saving);
    try {
      await kernel.current.updateEntity('User', effectiveUserId, {
        phoneNumber: normalizedPhone,
      });
      setPhoneNumber(normalizedPhone);
      toast.success(t.phoneSaved, { id: toastId });
    } catch (error) {
      toast.error(readErrorMessage(error, t.phoneSaveFailed), { id: toastId });
    } finally {
      setIsSavingPhone(false);
    }
  }, [isSavingPhone, kernel, phoneNumber, t]);

  const handlePasswordSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !user
      || isSavingPassword
      || kernel.current?.getUserContext().isImpersonating
    ) {
      return;
    }

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setPasswordError(t.passwordRequired);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t.passwordMismatch);
      return;
    }
    if (user.passwordEnabled && !currentPassword.trim()) {
      setPasswordError(t.currentPasswordRequired);
      return;
    }

    setPasswordError(null);
    setIsSavingPassword(true);
    const toastId = toast.loading(t.updating);
    try {
      await user.updatePassword({
        currentPassword: user.passwordEnabled ? currentPassword : undefined,
        newPassword,
        signOutOfOtherSessions: false,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(t.passwordUpdated, { id: toastId });
    } catch (error) {
      toast.error(readErrorMessage(error, t.passwordUpdateFailed), { id: toastId });
    } finally {
      setIsSavingPassword(false);
    }
  }, [
    confirmPassword,
    currentPassword,
    isSavingPassword,
    kernel,
    newPassword,
    t,
    user,
  ]);

  const showProfile = view === 'profile' || view === 'all';
  const showSecurity = view === 'security' || view === 'all';
  const pageTitle = showSecurity && !showProfile ? t.security : t.title;
  const pageSubtitle = showSecurity && !showProfile
    ? (language === 'fr' ? 'Gérez votre mot de passe et la sécurité de connexion.' : 'Manage your password and sign-in security.')
    : t.subtitle;

  return (
    <div className="w-full text-white">
      <header className="mb-8">
        <h1 className="text-[28px] font-normal leading-9 text-white">{pageTitle}</h1>
        <p className="mt-[38px] text-[15px] leading-[21px] text-[#ECECEC]">{pageSubtitle}</p>
      </header>

      <div>
        {showProfile ? <section aria-labelledby="account-profile-heading">
          <h2
            id="account-profile-heading"
            className="text-[17px] font-medium leading-6 text-[#ECECEC]"
          >
            {t.profile}
          </h2>
          <div className="mt-1 divide-y divide-white/[0.08]">
            <div className={`${SETTINGS_ROW_CLASS_NAME} lg:items-center`}>
              <div className="min-w-0">
                <p className="text-[15px] leading-[21px] text-[#ECECEC]">{t.photo}</p>
                {canEditProfile ? <p className="mt-1 text-[14px] leading-5 text-[#8E8E8E]">{t.avatarHint}</p> : null}
              </div>
              <div className="flex justify-start lg:justify-end">
                {userContext.dbUserId ? (
                  <UserAvatar
                    userId={userContext.dbUserId}
                    name={displayName}
                    canEdit={canEditProfile}
                    size={38}
                    shape="circle"
                    onAvatarUpdated={handleAvatarUpdated}
                  />
                ) : (
                  <div className="h-[38px] w-[38px] shrink-0 rounded-full bg-white/[0.04]" aria-hidden="true" />
                )}
              </div>
            </div>

            <div className={`${SETTINGS_ROW_CLASS_NAME} lg:items-center`}>
              <p className="text-[15px] leading-[21px] text-[#ECECEC]">{t.name}</p>
              <p className="min-w-0 truncate text-[15px] text-[#AFAFAF] lg:text-right">{displayName}</p>
            </div>

            <div className={`${SETTINGS_ROW_CLASS_NAME} lg:items-center`}>
              <p className="text-[15px] leading-[21px] text-[#ECECEC]">{t.email}</p>
              <p className="min-w-0 truncate text-[15px] text-[#AFAFAF] lg:text-right">{email || '\u2014'}</p>
            </div>

            <div className={`${SETTINGS_ROW_CLASS_NAME} lg:items-start`}>
              <div className="min-w-0">
                <p id="account-phone-heading" className="text-[15px] leading-[21px] text-[#ECECEC]">{t.phone}</p>
                <p className="mt-1 text-[14px] leading-5 text-[#8E8E8E]">{t.phoneHint}</p>
              </div>
              <form className="w-full" onSubmit={handlePhoneSubmit} noValidate aria-labelledby="account-phone-heading">
                <div className="flex min-w-0 flex-col gap-2 lg:flex-row">
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    aria-label={t.phone}
                    aria-invalid={Boolean(phoneError)}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder={t.phonePlaceholder}
                    disabled={!canEditProfile || isSavingPhone}
                    className={`${INPUT_CLASS_NAME} min-w-0 flex-1`}
                    style={BUTTON_STYLE}
                  />
                  <button
                    type="submit"
                    disabled={!canEditProfile || isSavingPhone}
                    className={BUTTON_CLASS_NAME}
                    style={BUTTON_STYLE}
                  >
                    {isSavingPhone ? t.saving : t.savePhone}
                  </button>
                </div>
                {phoneError ? (
                  <p className="mt-1.5 text-[14px] text-[#F28B82]" role="alert">{phoneError}</p>
                ) : null}
              </form>
            </div>
          </div>
        </section> : null}

        {showProfile ? <MemorySettingsSection /> : null}

        {showSecurity ? <section className={showProfile ? 'mt-8' : ''} aria-labelledby="account-security-heading">
          <h2
            id="account-security-heading"
            className="text-[17px] font-medium leading-6 text-[#ECECEC]"
          >
            {t.signIn}
          </h2>
          <div className="mt-1 divide-y divide-white/[0.08]">
            <div className={`${SETTINGS_ROW_CLASS_NAME} lg:items-start`}>
              <div className="min-w-0">
                <p id="account-password-heading" className="text-[15px] leading-[21px] text-[#ECECEC]">{t.password}</p>
                <p className={`mt-1 text-[14px] leading-5 ${isImpersonating ? 'text-[#F28B82]' : 'text-[#8E8E8E]'}`}>
                  {isImpersonating
                    ? t.impersonationHint
                    : user?.passwordEnabled
                      ? t.passwordExistingHint
                      : t.passwordNewHint}
                </p>
              </div>
              <form className="flex w-full flex-col gap-2" onSubmit={handlePasswordSubmit} noValidate aria-labelledby="account-password-heading">
                {!isImpersonating && user?.passwordEnabled ? (
                  <input
                    type="password"
                    autoComplete="current-password"
                    aria-label={t.currentPassword}
                    value={currentPassword}
                    onChange={handleCurrentPasswordChange}
                    placeholder={t.currentPassword}
                    disabled={isSavingPassword}
                    className={INPUT_CLASS_NAME}
                  style={BUTTON_STYLE}
                  />
                ) : null}
                <input
                  type="password"
                  autoComplete="new-password"
                  aria-label={t.newPassword}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  placeholder={t.newPassword}
                  disabled={!canUpdatePassword || isSavingPassword}
                  className={INPUT_CLASS_NAME}
                  style={BUTTON_STYLE}
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  aria-label={t.confirmPassword}
                  aria-invalid={Boolean(passwordError)}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder={t.confirmPassword}
                  disabled={!canUpdatePassword || isSavingPassword}
                  className={INPUT_CLASS_NAME}
                  style={BUTTON_STYLE}
                />
                {passwordError ? (
                  <p className="text-[14px] text-[#F28B82]" role="alert">{passwordError}</p>
                ) : null}
                <button
                  type="submit"
                  disabled={!canUpdatePassword || isSavingPassword}
                  className={`${BUTTON_CLASS_NAME} self-start lg:self-end`}
                  style={BUTTON_STYLE}
                >
                  {isSavingPassword ? t.updating : t.updatePassword}
                </button>
              </form>
            </div>
          </div>
        </section> : null}
      </div>
    </div>
  );
});

AccountSettingsPage.displayName = 'AccountSettingsPage';

export default AccountSettingsPage;
